import { useState, useEffect } from "react";
import apiClient from "@/api/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { User, CreditCard, CheckCircle, AlertCircle, Calendar } from "lucide-react";
import { useIdValidation, formatSouthAfricanId } from "@/utils/saIdValidation";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export default function ProfileCompletion({ onComplete }) {
  const { user, refetchUser } = useAuth();
  const queryClient = useQueryClient();
  
  // Extract first and last name from Google profile name if available
  const extractNamesFromGoogleName = (fullName) => {
    if (!fullName) return { firstName: "", lastName: "" };
    try {
      const parts = fullName.trim().split(" ");
      if (parts.length === 1) {
        return { firstName: parts[0], lastName: "" };
      }
      const firstName = parts[0];
      const lastName = parts.slice(1).join(" ");
      return { firstName, lastName };
    } catch (error) {
      console.warn("Error parsing Google name:", fullName, error);
      return { firstName: "", lastName: "" };
    }
  };

  const googleNames = user?.name ? extractNamesFromGoogleName(user.name) : { firstName: "", lastName: "" };
  
  // Safe date conversion function
  const formatDateForInput = (date) => {
    if (!date) return "";
    try {
      const dateObj = new Date(date);
      if (isNaN(dateObj.getTime())) return "";
      return dateObj.toISOString().split('T')[0];
    } catch (error) {
      console.warn("Invalid date format:", date);
      return "";
    }
  };

  const [formData, setFormData] = useState({
    firstName: user?.firstName || googleNames.firstName || "",
    lastName: user?.lastName || googleNames.lastName || "",
    identificationNumber: user?.identificationNumber || "",
    gender: user?.gender || "",
    dateOfBirth: formatDateForInput(user?.dateOfBirth),
  });
  
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // SA ID validation
  const idValidation = useIdValidation(formData.identificationNumber);

  // Auto-populate gender and date of birth when SA ID is validated
  useEffect(() => {
    if (idValidation.isValid && idValidation.details && formData.identificationNumber.length === 13) {
      setFormData(prev => ({
        ...prev,
        // Only auto-populate if fields are empty
        gender: prev.gender || idValidation.details.gender.toLowerCase(),
        dateOfBirth: prev.dateOfBirth || `${idValidation.details.dateOfBirth.year}-${String(idValidation.details.dateOfBirth.month).padStart(2, '0')}-${String(idValidation.details.dateOfBirth.day).padStart(2, '0')}`
      }));
    }
  }, [idValidation.isValid, idValidation.details, formData.identificationNumber]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Format ID number as user types
    if (name === 'identificationNumber') {
      // Remove non-digits and limit to 13 characters
      const cleanValue = value.replace(/\D/g, '').substring(0, 13);
      setFormData(prev => ({
        ...prev,
        [name]: cleanValue
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleGenderChange = (value) => {
    setFormData(prev => ({
      ...prev,
      gender: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    // Validate SA ID before submitting
    if (!idValidation.isValid && !idValidation.isEmpty) {
      setError(`Invalid South African ID number: ${idValidation.errors.join(', ')}`);
      setIsLoading(false);
      return;
    }

    try {
      // Update user profile with additional details
      await apiClient.put("/api/auth/patient/complete-profile", formData);
      
      // Invalidate auth query to refetch updated user
      await queryClient.invalidateQueries(["auth"]);
      await refetchUser();
      
      // Call the onComplete callback
      if (onComplete) {
        onComplete();
      }
    } catch (err) {
      setError(err.message || "Failed to complete profile");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 via-blue-600 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg">
              <User className="w-8 h-8 text-white" />
            </div>
          </div>
          <CardTitle className="text-2xl font-bold text-slate-900">
            Complete Your Profile
          </CardTitle>
          <p className="text-slate-600 mt-2">
            Welcome{user?.name ? `, ${user.name}` : ""}! Please complete your profile to access all features.
          </p>
          {user?.email && (
            <p className="text-sm text-slate-500 mt-1">
              Account: {user.email}
            </p>
          )}
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label
                  htmlFor="firstName"
                  className="text-sm font-medium text-slate-700 flex items-center gap-2"
                >
                  <User className="w-4 h-4" />
                  First Name
                </label>
                <Input
                  id="firstName"
                  name="firstName"
                  type="text"
                  value={formData.firstName}
                  onChange={handleChange}
                  placeholder="Enter your first name"
                  required
                />
              </div>

              <div className="space-y-2">
                <label
                  htmlFor="lastName"
                  className="text-sm font-medium text-slate-700 flex items-center gap-2"
                >
                  <User className="w-4 h-4" />
                  Last Name
                </label>
                <Input
                  id="lastName"
                  name="lastName"
                  type="text"
                  value={formData.lastName}
                  onChange={handleChange}
                  placeholder="Enter your last name"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <label
                htmlFor="identificationNumber"
                className="text-sm font-medium text-slate-700 flex items-center gap-2"
              >
                <CreditCard className="w-4 h-4" />
                South African ID Number
                {formData.identificationNumber && (
                  idValidation.isValid ? (
                    <CheckCircle className="w-4 h-4 text-green-500" />
                  ) : !idValidation.isEmpty && (
                    <AlertCircle className="w-4 h-4 text-red-500" />
                  )
                )}
              </label>
              <div className="relative">
                <Input
                  id="identificationNumber"
                  name="identificationNumber"
                  type="text"
                  value={formData.identificationNumber}
                  onChange={handleChange}
                  placeholder="9001015009087"
                  required
                  maxLength={13}
                  className={`${
                    formData.identificationNumber && !idValidation.isEmpty
                      ? idValidation.isValid
                        ? 'border-green-300 focus:border-green-400'
                        : 'border-red-300 focus:border-red-400'
                      : ''
                  }`}
                />
                {formData.identificationNumber && formData.identificationNumber.length === 13 && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-xs font-mono text-slate-500">
                    {formatSouthAfricanId(formData.identificationNumber)}
                  </div>
                )}
              </div>
              
              {/* Validation feedback */}
              {formData.identificationNumber && !idValidation.isEmpty && (
                <div className="space-y-1">
                  {idValidation.isValid ? (
                    <div className="text-xs text-green-600 space-y-1">
                      <p className="flex items-center gap-1">
                        <CheckCircle className="w-3 h-3" />
                        Valid South African ID number
                      </p>
                      {idValidation.details && (
                        <div className="ml-4 space-y-1 text-slate-600">
                          <p>Gender: {idValidation.details.gender}</p>
                          <p>Citizenship: {idValidation.details.citizenship}</p>
                          <p>Date of birth: {idValidation.details.dateOfBirth.day}/{idValidation.details.dateOfBirth.month}/{idValidation.details.dateOfBirth.year}</p>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-xs text-red-600">
                      {idValidation.errors.map((error, index) => (
                        <p key={index} className="flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" />
                          {error}
                        </p>
                      ))}
                    </div>
                  )}
                </div>
              )}
              
              <p className="text-xs text-slate-500">
                Enter your 13-digit South African ID number
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label
                  htmlFor="gender"
                  className="text-sm font-medium text-slate-700 flex items-center gap-2"
                >
                  <User className="w-4 h-4" />
                  Gender
                </label>
                <Select
                  value={formData.gender}
                  onValueChange={handleGenderChange}
                  required
                >
                  <SelectTrigger id="gender">
                    <SelectValue placeholder="Select gender" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="male">Male</SelectItem>
                    <SelectItem value="female">Female</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <label
                  htmlFor="dateOfBirth"
                  className="text-sm font-medium text-slate-700 flex items-center gap-2"
                >
                  <Calendar className="w-4 h-4" />
                  Date of Birth
                </label>
                <Input
                  id="dateOfBirth"
                  name="dateOfBirth"
                  type="date"
                  value={formData.dateOfBirth}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-xs text-blue-800">
                <strong>Why we need this information:</strong> Your ID number is required for medical 
                record keeping and appointment booking. This information is kept strictly confidential 
                and is only accessible to authorized healthcare providers.
              </p>
              {user?.name && (formData.firstName || formData.lastName) && (
                <p className="text-xs text-blue-700 mt-1">
                  <strong>Note:</strong> We&apos;ve pre-filled your name from your Google account.
                </p>
              )}
            </div>

            <Button 
              type="submit" 
              className="w-full" 
              disabled={isLoading || (formData.identificationNumber && !idValidation.isValid)}
            >
              {isLoading ? "Completing Profile..." : "Complete Profile"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}